import { useState, useEffect } from 'react';
import { useApp } from '../App';
import api from '../api';
import BackButton from '../components/BackButton';
import { useI18n } from '../i18n/index.jsx';

export default function Calendar() {
  const { user } = useApp();
  const { t } = useI18n();
  const [month, setMonth] = useState(new Date().getMonth());
  const [year, setYear] = useState(new Date().getFullYear());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const months = t('calendar.months');
  const weekDays = t('calendar.weekdays');

  const taxDeadlines = [
    { day: 7, name: 'PIT-5/CIT-2', color: '#f59e0b' },
    { day: 15, name: 'ZUS', color: '#ef4444' },
    { day: 20, name: 'PIT-4R', color: '#8b5cf6' },
    { day: 25, name: 'VAT/JPK', color: '#3b82f6' },
  ];

  useEffect(() => {
    loadEvents();
  }, [month, year]);

  async function loadEvents() {
    try {
      const { data } = await api.get(`/calendar/events?year=${year}&month=${month + 1}`);
      setEvents(data || []);
    } catch (e) {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }

  function getDaysInMonth(year, month) {
    return new Date(year, month + 1, 0).getDate();
  }

  function getFirstDayOfMonth(year, month) {
    const day = new Date(year, month, 1).getDay();
    return day === 0 ? 6 : day - 1;
  }

  function prevMonth() {
    if (month === 0) { setMonth(11); setYear(year - 1); } else { setMonth(month - 1); }
  }

  function nextMonth() {
    if (month === 11) { setMonth(0); setYear(year + 1); } else { setMonth(month + 1); }
  }

  function getEventsForDay(day) {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(e => e.date === dateStr);
  }

  function getTaxDeadline(day) {
    return taxDeadlines.find(d => d.day === day);
  }

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const today = new Date();
  const isCurrentMonth = today.getFullYear() === year && today.getMonth() === month;

  const days = [];
  for (let i = 0; i < firstDay; i++) { days.push(null); }
  for (let i = 1; i <= daysInMonth; i++) { days.push(i); }

  return (
    <div className="page">
      <BackButton />
      <h1>📅 {t('calendar.title')}</h1>

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16,
      }}>
        <button className="btn btn-small" onClick={prevMonth}>←</button>
        <div style={{ fontSize: 18, fontWeight: 'bold' }}>
          {Array.isArray(months) ? months[month] : month} {year}
        </div>
        <button className="btn btn-small" onClick={nextMonth}>→</button>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: 2,
        marginBottom: 8,
      }}>
        {Array.isArray(weekDays) && weekDays.map(day => (
          <div key={day} style={{
            textAlign: 'center',
            padding: 8,
            fontSize: 12,
            fontWeight: 'bold',
            opacity: 0.7,
          }}>
            {day}
          </div>
        ))}
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: 2,
        marginBottom: 24,
      }}>
        {days.map((day, index) => {
          if (!day) return <div key={index} />;
          
          const dayEvents = getEventsForDay(day);
          const taxDeadline = getTaxDeadline(day);
          const isToday = isCurrentMonth && today.getDate() === day;
          
          return (
            <div key={index} style={{
              background: isToday ? 'rgba(99,102,241,0.3)' : 'rgba(255,255,255,0.05)',
              borderRadius: 8,
              padding: 8,
              minHeight: 60,
              border: isToday ? '2px solid #6366f1' : '2px solid transparent',
            }}>
              <div style={{
                fontWeight: isToday ? 'bold' : 'normal',
                marginBottom: 4,
              }}>
                {day}
              </div>
              
              {taxDeadline && (
                <div style={{
                  background: taxDeadline.color,
                  borderRadius: 4,
                  padding: '2px 4px',
                  fontSize: 8,
                  marginBottom: 2,
                }}>
                  {taxDeadline.name}
                </div>
              )}
              
              {dayEvents.slice(0, 2).map((event, i) => (
                <div key={i} style={{
                  background: 'rgba(16,185,129,0.3)',
                  borderRadius: 4,
                  padding: '2px 4px',
                  fontSize: 8,
                  marginBottom: 2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {event.title}
                </div>
              ))}
            </div>
          );
        })}
      </div>

      <div style={{
        background: 'rgba(255,255,255,0.05)',
        borderRadius: 12,
        padding: 16,
        marginBottom: 16,
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: 12 }}>📋 {t('calendar.standard_deadlines')}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
          {taxDeadlines.map(td => (
            <div key={td.day} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{
                width: 12,
                height: 12,
                borderRadius: 4,
                background: td.color,
              }} />
              <span style={{ fontSize: 13 }}>{td.day}-е: {td.name}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{
        background: 'rgba(255,255,255,0.05)',
        borderRadius: 12,
        padding: 16,
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: 12 }}>⏰ {t('calendar.upcoming_events')}</div>
        {events.length === 0 ? (
          <div style={{ textAlign: 'center', opacity: 0.5, padding: 16 }}>
            {t('calendar.no_events')}
          </div>
        ) : (
          events.map((event, i) => (
            <div key={i} style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '8px 0',
              borderBottom: i < events.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none',
            }}>
              <span>{event.title}</span>
              <span style={{ opacity: 0.7, fontSize: 12 }}>{event.date.split('-').reverse().join('.')}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
